/**
 * 应用外壳：设备框 + 视图区 + 底部五标签 + 全局弹层（消息中心 / 应急 SOS）。
 *
 * 与原型的差别：
 *  - 底部标签**由口径常量驱动**（`@hew3/shared` 的 navigationCaliber：叁 / 三康 / 报告 / 服务 / 我的），
 *    组件里不再另存一份标签数组 —— 换一次口径只改常量，不会出现「导航改了、常量没改」的双份事实；
 *  - 标签高亮不再靠硬编码 TABMAP 字典，而是由路由匹配得出（react-router 的 NavLink）；
 *  - 视图区滚动位置在切路由时复位（原型每次整屏 innerHTML 替换，天然复位，改组件化后必须显式处理）；
 *  - 状态栏在真实手机上被系统状态栏取代，因此窄屏隐藏。
 */
import { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { navigationCaliber } from '@hew3/shared';
import { useClock, useDeviceScale, useScrollReset } from '../hooks/useDeviceScale.ts';
import { useAuth } from '../store/auth.ts';
import { useUi } from '../store/ui.ts';
import { trackPageReady, trackPageView } from '../lib/analytics.ts';
import { MessagesSheet } from '../features/profile/MessagesSheet.tsx';
import { SosSheet } from '../features/service/SosSheet.tsx';
import { Toast } from './Toast.tsx';

/**
 * 图标按口径 id 取：文案与顺序都来自 navigationCaliber，
 * 这里只补「画什么图形」这一件常量里没有的事，因此不会出现两份互相矛盾的导航定义。
 */
const TAB_ICONS: Record<string, string> = {
  // 叁：三螺旋的三个节点 + 上中下三条通路，对应「三个维度一起看」
  agent: 'M12 4v16M8 6.5h8M8 12h8M8 17.5h8M12 8.5a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6zM8 13.3a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6zM12 18.8a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6z',
  // 三康：加权几何平均的合流形状
  sk: 'M12 3v6M12 9 4.5 20M12 9l7.5 11M6 20h12M9 6h6',
  report: 'M7 3h8l4 4v14H7zM15 3v4h4M10 12h5M10 16h5',
  svc: 'M12 3l7 3v6c0 5-3.2 8-7 9-3.8-1-7-4-7-9V6z',
  me: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4 3.6-6 8-6s8 2 8 6',
};

/** 口径 id → 路由。路由不进常量表（那是前端路由实现细节），但必须与 navigationCaliber 一一对应。 */
const TAB_ROUTES: Record<string, string> = {
  agent: '/ai',
  sk: '/sk',
  report: '/health/reports',
  svc: '/service',
  me: '/profile',
};

const tabs = navigationCaliber.map((item) => ({
  to: TAB_ROUTES[item.id] ?? '/ai',
  label: item.label,
  title: item.title,
  icon: TAB_ICONS[item.id] ?? TAB_ICONS.agent,
}));

export function AppShell() {
  const deviceRef = useDeviceScale();
  const time = useClock();
  const viewportRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const member = useAuth((s) => s.member);
  const unread = useUi((s) => s.sheet);

  useScrollReset([location.pathname], viewportRef.current);

  /* ---------------- 统一埋点：路由到达与「内容可见耗时」 ---------------- */

  const isFetching = useIsFetching();
  const previousPath = useRef<string | null>(null);
  const enteredAt = useRef(0);
  const readyFor = useRef<string | null>(null);

  useEffect(() => {
    enteredAt.current = performance.now();
    readyFor.current = null;
    trackPageView(location.pathname, previousPath.current ?? undefined);
    previousPath.current = location.pathname;
  }, [location.pathname]);

  /**
   * 取数结束 → 等一帧（确保内容已提交绘制）→ 上报「内容可见」。
   * 这是「3 秒建立三螺旋认知」的可测代理指标：从进入路由到用户真的能看到内容。
   * 每个路由只上报一次，避免后台轮询反复计入。
   */
  useEffect(() => {
    const route = location.pathname;
    if (readyFor.current === route || isFetching > 0) return;
    const raf = requestAnimationFrame(() => {
      if (readyFor.current === route) return;
      readyFor.current = route;
      trackPageReady(route, performance.now() - enteredAt.current);
    });
    return () => cancelAnimationFrame(raf);
  }, [isFetching, location.pathname]);

  return (
    <div className="app-root">
      <div className="stage">
        <div className="device-wrap">
          <div className="device" ref={deviceRef}>
            <div className="device-shadow" />
            <div className="screen">
              <div className="notch" />
              <div className="statusbar">
                <span className="sb-time">{time}</span>
                <span className="sb-right">
                  <svg viewBox="0 0 20 12" className="sb-ico">
                    <rect x="0" y="8" width="3" height="4" rx="1" />
                    <rect x="4.5" y="6" width="3" height="6" rx="1" />
                    <rect x="9" y="3.5" width="3" height="8.5" rx="1" />
                    <rect x="13.5" y="1" width="3" height="11" rx="1" />
                  </svg>
                  <svg viewBox="0 0 16 12" className="sb-ico">
                    <path d="M8 10.6 5.6 8.2a3.4 3.4 0 0 1 4.8 0zM3.2 5.8 1.4 4a9.3 9.3 0 0 1 13.2 0l-1.8 1.8a6.8 6.8 0 0 0-9.6 0z" />
                  </svg>
                  <span className="sb-bat">
                    <i />
                  </span>
                </span>
              </div>

              <main className="viewport" ref={viewportRef}>
                <Outlet />
              </main>

              {member && (
                <nav className="tabbar">
                  {tabs.map((t) => (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      // 五个标签都是一级页面，精确匹配即可：避免在二级页面（如 /health/reports）误亮「报告」以外的标签
                      end
                      title={t.title}
                      className={({ isActive }) => `tab${isActive ? ' on' : ''}`}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d={t.icon} />
                      </svg>
                      <span>{t.label}</span>
                    </NavLink>
                  ))}
                </nav>
              )}
            </div>
          </div>
          <div className="device-caption">390 × 844 · 手机端为主形态</div>
        </div>
      </div>

      <MessagesSheet />
      <SosSheet />
      <Toast />

      {/* 开发期自检用：sheet 状态挂在外壳上，便于 e2e 断言 */}
      <span hidden data-sheet={unread ?? ''} />
    </div>
  );
}
